import { useCallback, useEffect, useState } from 'react'
import type { Bill, FinancialAccount } from '../../types/family-finance'
import { categoryOf, isPayableAccount, nextDueDate } from '../../types/family-finance'
import {
  addBill,
  billStatusOf,
  listAccounts,
  listBills,
  markBillPaid,
  setPaymentTerms,
  updateBill,
} from '../../lib/family-finance-api'
import {
  Card,
  Chip,
  LoadingRows,
  daysUntil,
  inputClass,
  money,
  primaryBtn,
  secondaryBtn,
  shortDate,
  LoadError,
  errorMessage,
} from './shared'
import type { ChipTone } from './shared'

const STATUS_META: Record<string, { label: string; tone: ChipTone }> = {
  overdue: { label: 'Overdue', tone: 'red' },
  'due-soon': { label: 'Due soon', tone: 'amber' },
  upcoming: { label: 'Upcoming', tone: 'blue' },
  paid: { label: 'Paid', tone: 'green' },
}

/**
 * Turn cards and loans into bills.
 *
 * Every credit card and mortgage is a payment with a due date, but the sync
 * can't say what or when: SimpleFIN authorizes balances only, and the handful
 * of bridges that pass a minimum payment through `extra` are the exception. So
 * the minimum and the due day are entered once here, stored on the account so
 * they survive syncs, and turned into a Bill — which is what the reminder
 * Lambda already watches.
 */
function CardsAndLoansCard({
  accounts,
  bills,
  onChanged,
}: {
  accounts: FinancialAccount[]
  bills: Bill[]
  onChanged: () => Promise<unknown> | void
}) {
  const [drafts, setDrafts] = useState<Record<string, { min: string; day: string }>>({})
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const payable = accounts.filter(isPayableAccount)
  const billed = new Set(bills.map((b) => b.linkedAccountId).filter(Boolean))
  const unbilled = payable.filter((a) => !billed.has(a.id))

  if (payable.length === 0) return null

  function draftFor(account: FinancialAccount) {
    return (
      drafts[account.id] ?? {
        min: account.minimumPayment != null ? String(account.minimumPayment) : '',
        day: account.dueDay != null ? String(account.dueDay) : '',
      }
    )
  }

  async function addFor(account: FinancialAccount) {
    const { min, day } = draftFor(account)
    const amount = parseFloat(min)
    const dueDay = parseInt(day, 10)
    if (isNaN(amount) || amount <= 0 || isNaN(dueDay) || dueDay < 1 || dueDay > 31) {
      setError(`${account.name}: enter a minimum payment and a due day between 1 and 31.`)
      return
    }
    setBusyId(account.id)
    setError(null)
    try {
      // Terms on the account, so a later sync and a regenerated bill both keep them.
      await setPaymentTerms(account.id, {
        minimumPayment: amount,
        dueDay,
        autopay: account.autopay ?? false,
      })
      await addBill({
        name: account.name,
        payee: account.institutionName,
        amount,
        nextDueDate: nextDueDate(dueDay),
        frequency: 'monthly',
        autopay: account.autopay ?? false,
        linkedAccountId: account.id,
        linkedAccountName: `${account.institutionName} · ${account.name}`,
        category: categoryOf(account.type) === 'mortgage' ? 'Housing' : 'Debt',
        reminderDaysBefore: 5,
        emailReminder: true,
      })
      await onChanged()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Card
      title="Cards & loans"
      subtitle={
        unbilled.length === 0
          ? 'Every card and loan already has a bill'
          : 'The balance syncs; the minimum payment and due date do not — add them once and each becomes a reminder'
      }
    >
      {error && <p className="text-red-600 dark:text-red-400 text-sm mb-3">{error}</p>}
      <div className="space-y-2">
        {payable.map((account) => {
          const linked = billed.has(account.id)
          const draft = draftFor(account)
          return (
            <div
              key={account.id}
              className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-100/50 dark:bg-slate-900/50 px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="text-slate-800 dark:text-slate-200 text-sm truncate">
                  {account.name}
                  <span className="text-slate-500"> · {account.institutionName}</span>
                </p>
                <p className="text-slate-500 text-xs">
                  {money(account.balance, 2)} balance ·{' '}
                  {categoryOf(account.type) === 'mortgage' ? 'Mortgage' : 'Credit card'}
                </p>
              </div>
              {linked ? (
                <Chip tone="green">in bills</Chip>
              ) : (
                <>
                  <input
                    value={draft.min}
                    onChange={(e) =>
                      setDrafts({ ...drafts, [account.id]: { ...draft, min: e.target.value } })
                    }
                    placeholder="Min payment"
                    type="number"
                    step="0.01"
                    className="w-28 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm rounded-lg px-2 py-1.5 border border-slate-300 dark:border-slate-600 outline-none"
                  />
                  <input
                    value={draft.day}
                    onChange={(e) =>
                      setDrafts({ ...drafts, [account.id]: { ...draft, day: e.target.value } })
                    }
                    placeholder="Due day"
                    type="number"
                    min="1"
                    max="31"
                    className="w-20 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm rounded-lg px-2 py-1.5 border border-slate-300 dark:border-slate-600 outline-none"
                  />
                  <button
                    onClick={() => addFor(account)}
                    disabled={busyId === account.id}
                    className={secondaryBtn}
                  >
                    {busyId === account.id ? 'Adding…' : 'Add to bills'}
                  </button>
                </>
              )}
            </div>
          )
        })}
      </div>
    </Card>
  )
}

function AddBillForm({ onAdded, onClose }: { onAdded: () => void; onClose: () => void }) {
  const [name, setName] = useState('')
  const [payee, setPayee] = useState('')
  const [amount, setAmount] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [frequency, setFrequency] = useState<Bill['frequency']>('monthly')
  const [autopay, setAutopay] = useState(false)
  const [reminderDays, setReminderDays] = useState('5')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const parsedAmount = parseFloat(amount)
    if (!name.trim() || !payee.trim() || !dueDate || isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Name, payee, a positive amount, and a due date are required.')
      return
    }
    setSaving(true)
    try {
      await addBill({
        name: name.trim(),
        payee: payee.trim(),
        amount: parsedAmount,
        nextDueDate: dueDate,
        frequency,
        autopay,
        linkedAccountId: null,
        linkedAccountName: null,
        category: 'Other',
        reminderDaysBefore: parseInt(reminderDays, 10) || 5,
        emailReminder: true,
      })
      onAdded()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add bill')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card title="Add a bill">
      <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Bill name (e.g. Lawn service)" className={inputClass} />
        <input value={payee} onChange={(e) => setPayee(e.target.value)} placeholder="Payee" className={inputClass} />
        <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" type="number" step="0.01" min="0" className={inputClass} />
        <input value={dueDate} onChange={(e) => setDueDate(e.target.value)} type="date" className={inputClass} />
        <select value={frequency} onChange={(e) => setFrequency(e.target.value as Bill['frequency'])} className={inputClass}>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="annual">Annual</option>
          <option value="one-time">One-time</option>
        </select>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <input type="checkbox" checked={autopay} onChange={(e) => setAutopay(e.target.checked)} className="accent-blue-500" />
            Autopay
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            Remind
            <input value={reminderDays} onChange={(e) => setReminderDays(e.target.value)} type="number" min="1" max="30" className="w-16 bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-sm rounded-lg px-2 py-1.5 border border-slate-300 dark:border-slate-600 outline-none" />
            days before
          </label>
        </div>
        {error && <p className="text-red-600 dark:text-red-400 text-sm sm:col-span-2">{error}</p>}
        <div className="flex gap-2 sm:col-span-2 justify-end">
          <button type="button" onClick={onClose} className={secondaryBtn}>Cancel</button>
          <button type="submit" disabled={saving} className={primaryBtn}>{saving ? 'Saving…' : 'Add bill'}</button>
        </div>
      </form>
    </Card>
  )
}

export default function BillsTab() {
  const [bills, setBills] = useState<Bill[]>([])
  const [accounts, setAccounts] = useState<FinancialAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    try {
      const [nextBills, nextAccounts] = await Promise.all([listBills(), listAccounts()])
      setBills(nextBills)
      setAccounts(nextAccounts)
      setLoadError(null)
    } catch (err) {
      setLoadError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  async function handlePaid(id: string) {
    setBusy(id)
    try {
      await markBillPaid(id)
      await reload()
    } finally {
      setBusy(null)
    }
  }

  async function toggleReminder(bill: Bill) {
    setBusy(bill.id)
    try {
      await updateBill(bill.id, { emailReminder: !bill.emailReminder })
      await reload()
    } finally {
      setBusy(null)
    }
  }

  if (loadError) return <LoadError error={loadError} onRetry={reload} />
  if (loading) return <LoadingRows rows={6} />

  const monthlyTotal = bills
    .filter((b) => b.frequency === 'monthly')
    .reduce((s, b) => s + b.amount, 0)
  const withReminders = bills.filter((b) => b.emailReminder).length

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-slate-500 dark:text-slate-400 text-sm">
          {bills.length} bills · {money(monthlyTotal)} recurring monthly · {withReminders} with email reminders
        </p>
        <button onClick={() => setShowAdd((v) => !v)} className={primaryBtn}>
          {showAdd ? 'Close' : '+ Add bill'}
        </button>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl px-4 py-3 text-sm text-blue-700 dark:text-blue-300">
        🔔 Email reminders send from <span className="font-medium">alerts@nathanaelbrown.info</span> ahead of each
        due date, and overdue bills trigger a same-day alert. Bills linked to a connected account auto-detect
        payment and roll to the next cycle.
      </div>

      {showAdd && <AddBillForm onAdded={reload} onClose={() => setShowAdd(false)} />}

      <CardsAndLoansCard accounts={accounts} bills={bills} onChanged={reload} />


      <Card title="All bills">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-500 text-xs uppercase tracking-wide border-b border-slate-200 dark:border-slate-700">
                <th className="text-left py-2 pr-3 font-medium">Bill</th>
                <th className="text-left py-2 pr-3 font-medium">Next due</th>
                <th className="text-right py-2 pr-3 font-medium">Amount</th>
                <th className="text-left py-2 pr-3 font-medium hidden md:table-cell">Payment</th>
                <th className="text-center py-2 pr-3 font-medium hidden sm:table-cell">Reminder</th>
                <th className="text-right py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {bills.map((b) => {
                const status = billStatusOf(b)
                const meta = STATUS_META[status]
                const days = daysUntil(b.nextDueDate)
                return (
                  <tr key={b.id} className="border-b border-slate-200/50 dark:border-slate-700/50 last:border-0">
                    <td className="py-3 pr-3">
                      <p className="text-slate-900 dark:text-white font-medium">{b.name}</p>
                      <p className="text-slate-500 text-xs">{b.payee} · {b.frequency}</p>
                    </td>
                    <td className="py-3 pr-3">
                      <p className="text-slate-800 dark:text-slate-200">{shortDate(b.nextDueDate)}</p>
                      <Chip tone={meta.tone}>
                        {status === 'paid' ? 'paid' : days < 0 ? `${-days}d late` : days === 0 ? 'today' : `in ${days}d`}
                      </Chip>
                    </td>
                    <td className="py-3 pr-3 text-right text-slate-900 dark:text-white font-medium">{money(b.amount, 2)}</td>
                    <td className="py-3 pr-3 hidden md:table-cell">
                      {b.autopay ? (
                        <Chip tone="green">autopay</Chip>
                      ) : (
                        <Chip tone="slate">manual</Chip>
                      )}
                      {b.linkedAccountName && (
                        <p className="text-slate-500 text-xs mt-1">{b.linkedAccountName}</p>
                      )}
                    </td>
                    <td className="py-3 pr-3 text-center hidden sm:table-cell">
                      <button
                        onClick={() => toggleReminder(b)}
                        disabled={busy === b.id}
                        title={b.emailReminder ? `Email ${b.reminderDaysBefore}d before due — click to disable` : 'Reminders off — click to enable'}
                        className={`text-lg ${b.emailReminder ? '' : 'opacity-30 grayscale'}`}
                      >
                        🔔
                      </button>
                      {b.emailReminder && <p className="text-slate-500 text-xs">{b.reminderDaysBefore}d before</p>}
                    </td>
                    <td className="py-3 text-right">
                      {status !== 'paid' && !b.autopay && (
                        <button onClick={() => handlePaid(b.id)} disabled={busy === b.id} className={secondaryBtn}>
                          {busy === b.id ? '…' : 'Mark paid'}
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
